import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import moment from 'moment';
import * as adminService from '../../services/admin';

const TdsDeductions = () => {
    const [tdsDeductions, setTdsDeductions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState({
        date_from: moment().startOf('month').format('YYYY-MM-DD'),
        date_to: moment().endOf('month').format('YYYY-MM-DD'),
        section_code: '',
        status: ''
    });

    const tdsSections = [
        { code: '194C', description: 'Payment to Contractors' },
        { code: '194J', description: 'Professional/Technical Services' },
        { code: '194A', description: 'Interest other than on Securities' },
        { code: '194I', description: 'Rent' },
        { code: '194H', description: 'Commission/Brokerage' },
        { code: '192', description: 'Salaries' },
        { code: '194D', description: 'Insurance Commission' },
        { code: '194G', description: 'Commission on Sale of Lottery Tickets' }
    ];

    useEffect(() => {
        loadTdsDeductions();
    }, []);

    const loadTdsDeductions = async () => {
        try {
            setLoading(true);
            const response = await adminService.listTdsDeductions(filter);
            if (response.status === 'success') {
                setTdsDeductions(response.data);
            }
        } catch (error) {
            console.error('Error loading TDS deductions:', error);
            toast.error('Failed to load TDS deductions');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilter({ ...filter, [name]: value });
    };

    const handleApplyFilter = () => {
        loadTdsDeductions();
    };

    const handleGenerateCertificate = async (id) => {
        try {
            const response = await adminService.generateTdsCertificate(id);
            if (response.status === 'success') {
                toast.success('TDS certificate generated successfully');
                loadTdsDeductions();
            } else {
                toast.error(response.message || 'Failed to generate certificate');
            }
        } catch (error) {
            console.error('Error generating certificate:', error);
            toast.error('Failed to generate certificate');
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            'pending': 'bg-warning',
            'paid': 'bg-success',
            'filed': 'bg-info'
        };
        return badges[status] || 'bg-secondary';
    };

    const columnDefs = [
        {
            headerName: 'Deduction Date',
            field: 'deduction_date',
            sortable: true,
            filter: true,
            width: 140,
            cellRenderer: (params) => moment(params.value).format('DD-MMM-YYYY')
        },
        {
            headerName: 'Party',
            field: 'party.account_name',
            sortable: true,
            filter: true,
            width: 200
        },
        {
            headerName: 'Section',
            field: 'section_code',
            sortable: true,
            filter: true,
            width: 110,
            cellRenderer: (params) => (
                <span className="badge bg-secondary">{params.value}</span>
            )
        },
        {
            headerName: 'PAN',
            field: 'pan_number',
            sortable: true,
            filter: true,
            width: 130
        },
        {
            headerName: 'Payment Amount',
            field: 'payment_amount',
            sortable: true,
            filter: true,
            width: 150,
            cellRenderer: (params) => `₹${parseFloat(params.value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
        },
        {
            headerName: 'TDS Rate',
            field: 'tds_rate',
            sortable: true,
            width: 100,
            cellRenderer: (params) => `${parseFloat(params.value).toFixed(2)}%`
        },
        {
            headerName: 'TDS Amount',
            field: 'tds_amount',
            sortable: true,
            filter: true,
            width: 140,
            cellRenderer: (params) => `₹${parseFloat(params.value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
        },
        {
            headerName: 'Status',
            field: 'status',
            sortable: true,
            filter: true,
            width: 110,
            cellRenderer: (params) => (
                <span className={`badge ${getStatusBadge(params.value)} text-capitalize`}>
                    {params.value}
                </span>
            )
        },
        {
            headerName: 'Certificate No.',
            field: 'certificate_number',
            sortable: true,
            filter: true,
            width: 150
        },
        {
            headerName: 'Actions',
            field: 'id',
            width: 120,
            cellRenderer: (params) => (
                <div>
                    {!params.data.certificate_number && (
                        <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleGenerateCertificate(params.value)}
                            title="Generate Certificate"
                        >
                            <i className="fa fa-certificate"></i>
                        </button>
                    )}
                </div>
            )
        }
    ];

    // Calculate summary
    const summary = {
        total_payment: tdsDeductions.reduce((sum, item) => sum + parseFloat(item.payment_amount || 0), 0),
        total_tds: tdsDeductions.reduce((sum, item) => sum + parseFloat(item.tds_amount || 0), 0),
        pending_count: tdsDeductions.filter(item => item.status === 'pending').length,
        paid_count: tdsDeductions.filter(item => item.status === 'paid').length,
        filed_count: tdsDeductions.filter(item => item.status === 'filed').length
    };

    return (
        <div className="container-fluid mt-3">
            <div className="card">
                <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="fa fa-file-invoice-dollar"></i> TDS Deductions
                    </h5>
                </div>
                <div className="card-body">
                    {/* Filters */}
                    <div className="row mb-3">
                        <div className="col-md-3">
                            <label className="form-label">From Date</label>
                            <input
                                type="date"
                                className="form-control"
                                name="date_from"
                                value={filter.date_from}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">To Date</label>
                            <input
                                type="date"
                                className="form-control"
                                name="date_to"
                                value={filter.date_to}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Section</label>
                            <select
                                className="form-select"
                                name="section_code"
                                value={filter.section_code}
                                onChange={handleFilterChange}
                            >
                                <option value="">All Sections</option>
                                {tdsSections.map(section => (
                                    <option key={section.code} value={section.code}>
                                        {section.code} - {section.description}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Status</label>
                            <select
                                className="form-select"
                                name="status"
                                value={filter.status}
                                onChange={handleFilterChange}
                            >
                                <option value="">All</option>
                                <option value="pending">Pending</option>
                                <option value="paid">Paid</option>
                                <option value="filed">Filed</option>
                            </select>
                        </div>
                        <div className="col-md-1 d-flex align-items-end">
                            <button
                                className="btn btn-primary w-100"
                                onClick={handleApplyFilter}
                            >
                                <i className="fa fa-filter"></i>
                            </button>
                        </div>
                    </div>

                    {/* Summary Cards */}
                    <div className="row mb-3">
                        <div className="col-md-3">
                            <div className="card bg-light">
                                <div className="card-body text-center">
                                    <h6 className="mb-2">Total Payment</h6>
                                    <h4 className="mb-0 text-primary">
                                        ₹{summary.total_payment.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </h4>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card bg-light">
                                <div className="card-body text-center">
                                    <h6 className="mb-2">Total TDS</h6>
                                    <h4 className="mb-0 text-danger">
                                        ₹{summary.total_tds.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </h4>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-2">
                            <div className="card bg-light">
                                <div className="card-body text-center">
                                    <h6 className="mb-2">Pending</h6>
                                    <h4 className="mb-0 text-warning">{summary.pending_count}</h4>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-2">
                            <div className="card bg-light">
                                <div className="card-body text-center">
                                    <h6 className="mb-2">Paid</h6>
                                    <h4 className="mb-0 text-success">{summary.paid_count}</h4>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-2">
                            <div className="card bg-light">
                                <div className="card-body text-center">
                                    <h6 className="mb-2">Filed</h6>
                                    <h4 className="mb-0 text-info">{summary.filed_count}</h4>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* TDS Grid */}
                    <div className="ag-theme-alpine" style={{ height: '400px', width: '100%' }}>
                        <AgGridReact
                            rowData={tdsDeductions}
                            columnDefs={columnDefs}
                            pagination={true}
                            paginationPageSize={20}
                            domLayout='autoHeight'
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TdsDeductions;
